import React, { useState } from "react";
import { useBotStatus } from "../hooks/useBotStatus";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
// usa theme via props do styled-components (ThemeProvider)
import {
  useLeads,
  useDeleteLead,
  queryKeys,
} from "../hooks/useApi";
import { useQueryClient } from '@tanstack/react-query';
import LeadsTable from "../components/Leads/LeadsTable";
import ConfirmationModal from "../components/Layout/ConfirmationModal";
import LeadDetailsModal from "../components/Leads/LeadDetailsModal"; // Importar o novo modal de detalhes
import { Lead } from "../types/api";
import { useRealtimeUpdates } from "../hooks/useRealtimeUpdates";
import SkeletonLoader from "../components/SkeletonLoader";
import FadeInContent from "../components/FadeInContent";
import { useSkeletonDelay } from "../hooks/useSkeletonDelay";

const LeadsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const LeadsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
`;

const LeadsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const LeadsInfo = styled.div`
  flex: 1;
`;

const LeadsName = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
  background: none;
  -webkit-background-clip: initial;
  -webkit-text-fill-color: initial;
  background-clip: initial;
`;

const LeadsDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const LeadsActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  flex-wrap: wrap;
`;

// filtros agora são renderizados dentro de `LeadsTable`

const ActionButton = styled.button<{ $variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast}
    ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.neon};
            background: ${props.theme.colors.primaryHover};
            color: ${props.theme.colors.textPrimary};
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[4]};
  text-align: center;
  transition: all ${props => props.theme.animations.duration.normal}
    ${props => props.theme.animations.easing.easeInOut};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const StatValue = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// StatChange não é usado nesta página

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<number[] | null>(null);
  const [clearSelectionSignal, setClearSelectionSignal] = useState(0);
  const [viewingLead, setViewingLead] = useState<Lead | null>(null); // Estado para o lead em visualização

  const queryClient = useQueryClient();
  const handleRealtimeEvent = React.useCallback((event: { type: string; payload: unknown }) => {
    const t = event.type;
    if (t && (t === 'lead_update' || t === 'lead_created' || t === 'lead_deleted' || t === 'dashboard_update')) {
      try {
        queryClient.invalidateQueries({ queryKey: queryKeys.leads });
        queryClient.invalidateQueries({ queryKey: queryKeys.leadsFollowUp });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
        // Força refetch imediato das ativas para refletir já na tela
        queryClient.refetchQueries({ queryKey: queryKeys.leads, type: 'active' });
        queryClient.refetchQueries({ queryKey: queryKeys.leadsFollowUp, type: 'active' });
        queryClient.refetchQueries({ queryKey: queryKeys.dashboardStats, type: 'active' });
      } catch (e) {
        console.error('Falha ao atualizar queries em tempo real:', e);
      }
    }
  }, [queryClient]);
  useRealtimeUpdates({ onEvent: handleRealtimeEvent });

  const {
    isLoading: botLoading,
    isActive: botActive,
    error: botError,
  } = useBotStatus();
  const { data: leadsData, isLoading: leadsLoading } = useLeads();
  const deleteLeadMutation = useDeleteLead();

  const leads = leadsData?.data?.leads || [];

  const handleEditLead = (lead: Lead) => {
    // Navega para a página de edição passando o lead via state
    navigate(`/leads/edit/${lead.id}`, { state: { lead } });
  };

  // Abre o modal de confirmação
  const handleDeleteRequest = (lead: Lead) => {
    setLeadToDelete(lead);
  };

  // Executa a exclusão após confirmação
  const handleConfirmDelete = async () => {
    if (!leadToDelete) return;

    try {
      await deleteLeadMutation.mutateAsync(leadToDelete.id);
    } catch (error) {
      console.error("Erro ao excluir lead:", error);
    } finally {
      setLeadToDelete(null); // Fecha o modal
      // Invalida as queries para atualizar a lista
      try {
        queryClient.invalidateQueries({ queryKey: queryKeys.leads });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      } catch (e) {
        console.error('Falha ao invalidar queries após exclusão:', e);
      }
    }
  };

  const handleBulkDeleteRequest = (leadIds: number[]) => {
    setBulkDeleteIds(leadIds);
  };

  const handleConfirmBulkDelete = async () => {
    if (!bulkDeleteIds || bulkDeleteIds.length === 0) return;
    try {
      await Promise.all(bulkDeleteIds.map(id => deleteLeadMutation.mutateAsync(id)));
      try {
        queryClient.invalidateQueries({ queryKey: queryKeys.leads });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      } catch (e) {
        console.error('Falha ao invalidar queries após exclusão múltipla:', e);
      }
    } catch (error) {
      console.error('Erro ao excluir leads (em lote):', error);
    } finally {
      setBulkDeleteIds(null);
      setClearSelectionSignal(prev => prev + 1);
    }
  };

  const handleViewLead = (lead: Lead) => {
    setViewingLead(lead);
  };

  // Hook para adicionar delay mínimo no skeleton (suavização)
  const showSkeleton = useSkeletonDelay(
    botLoading || !botActive || !!botError || leadsLoading, 
    1500
  );

  // Calcular estatísticas
  const stats = {
    total: leads.length,
    novos: leads.filter((lead) => lead.status === "novo").length,
    qualificados: leads.filter((lead) => lead.status === "qualificado").length,
    convertidos: leads.filter((lead) => lead.status === "convertido").length,
  };

  if (showSkeleton) {
    return <SkeletonLoader variant="leads" />;
  }

  return (
    <FadeInContent>
      <LeadsContainer>
      <LeadsHeader>
        <LeadsTitle>
          <LeadsInfo>
            <LeadsName>Gerenciamento de Leads</LeadsName>
            <LeadsDescription>
              Gerencie seus potenciais clientes e oportunidades
            </LeadsDescription>
          </LeadsInfo>
        </LeadsTitle>

        <LeadsActions>
          <Link to="/leads/new" style={{ textDecoration: 'none' }}>
            <ActionButton $variant="primary">
              <Plus size={16} />
              Novo Lead
            </ActionButton>
          </Link>
        </LeadsActions>
      </LeadsHeader>

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        >
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total de Leads</StatLabel>
          {/* Percentual removido */}
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatValue>{stats.novos}</StatValue>
          <StatLabel>Leads Novos</StatLabel>
          {/* Percentual removido */}
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatValue>{stats.qualificados}</StatValue>
          <StatLabel>Qualificados</StatLabel>
          {/* Percentual removido */}
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <StatValue>{stats.convertidos}</StatValue>
          <StatLabel>Convertidos</StatLabel>
          {/* Percentual removido */}
        </StatCard>
      </StatsGrid>

      {/* Leads Table */}
      <LeadsTable
        leads={leads}
        loading={leadsLoading}
        onEdit={handleEditLead}
        onDelete={handleDeleteRequest}
        onBulkDeleteRequest={handleBulkDeleteRequest}
        clearSelectionSignal={clearSelectionSignal}
        onView={handleViewLead} // Passar a função para o componente da tabela
        onCreate={() => {}}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!leadToDelete}
        onClose={() => setLeadToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message={
          <>
            Tem certeza que deseja excluir o lead{" "}
            <strong>{leadToDelete?.nome}</strong>? Esta ação não pode ser
            desfeita.
          </>
        }
        confirmText="Excluir"
        loading={deleteLeadMutation.isPending}
      />

      <ConfirmationModal
        isOpen={!!bulkDeleteIds}
        onClose={() => setBulkDeleteIds(null)}
        onConfirm={handleConfirmBulkDelete}
        title="Confirmar Exclusão em Lote"
        message={
          <>
            Tem certeza que deseja excluir {bulkDeleteIds?.length || 0} lead(s)? Esta ação não pode ser desfeita.
          </>
        }
        confirmText="Excluir Selecionados"
        loading={deleteLeadMutation.isPending}
      />

      {/* Modal de Detalhes do Lead */}
      <LeadDetailsModal
        isOpen={!!viewingLead}
        onClose={() => setViewingLead(null)}
        lead={viewingLead}
      />
    </LeadsContainer>
    </FadeInContent>
  );
};

export default Leads;
